/**
 * SOLOMON — MCP SERVERS SETTINGS PANEL
 *
 * Operator UI for managing Model Context Protocol (MCP) remote servers.
 * Security contract:
 *   • Token is WRITE-ONLY — field is type="password", cleared after submit.
 *     The API returns `hasToken` (boolean) only; the token value is NEVER
 *     rendered back to the frontend.
 *   • Blessing a tool to 'read' (auto-run) requires explicit confirmation.
 *     The confirmation dialog shows the sanitized tool description + server's
 *     advisory read/write hint so the operator can make an informed decision.
 *   • If a server's tool details haven't been loaded (e.g. initial GET list),
 *     the bless button is replaced with a "Refresh to review tool details"
 *     affordance — blessing blind is not allowed.
 *   • Revoking to 'gated' (approval required) needs no confirmation.
 *   • Drifted tools are shown with a "definition changed — re-review" warning
 *     and are already gated server-side; operator must re-bless explicitly.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { lastErrorCopy, loopbackRelayCommand, LOOPBACK_COPY } from './mcpCopy.js';
import './mcp-servers.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ── Icons (Lucide-style, 1.5px stroke) ───────────────────────────────────────

function McpPlugIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22V12" />
      <path d="M8 12H4a2 2 0 0 1-2-2V6l2-2h4" />
      <path d="M16 12h4a2 2 0 0 0 2-2V6l-2-2h-4" />
      <path d="M8 2v4" />
      <path d="M16 2v4" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function PowerIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </svg>
  );
}

function AlertTriangleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// Task 15: the classification pill's third arm (paid_read tools). Plan text
// claimed this already existed in the file; it did not — added here,
// matching every other icon's footprint/stroke conventions.
function DollarIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    connected: { cls: 'mcp-status--connected', label: 'Connected' },
    pending:   { cls: 'mcp-status--pending',   label: 'Pending' },
    error:     { cls: 'mcp-status--error',     label: 'Error' },
    disabled:  { cls: 'mcp-status--disabled',  label: 'Disabled' },
    awaiting_auth: { cls: 'mcp-status--pending', label: 'Sign-in required' },
  };
  const { cls, label } = map[status] || { cls: 'mcp-status--pending', label: status };
  return <span className={`mcp-status-badge ${cls}`}>{label}</span>;
}

// ── Server card ───────────────────────────────────────────────────────────────

/**
 * Loopback-assisted connect (Issue 113 follow-on). The backend returned
 * { authUrl, relay }; we show the exact relay command and wait — the browser
 * only leaves for the provider when the operator presses Continue, after the
 * relay is running on their machine. The command is built by
 * loopbackRelayCommand from the backend contract; if it refuses (non-https
 * target, bad port/path) we show badRelay copy and no command.
 */
function LoopbackRelayPanel({ loopback, onContinue, onCancel }) {
  const [copied, setCopied] = useState(false);
  const command = loopbackRelayCommand(loopback.relay);
  const copy = async () => {
    try { await navigator.clipboard.writeText(command); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard unavailable — the command is selectable text */ }
  };
  return (
    <div className="mcp-loopback" role="region" aria-label="Connect via this computer">
      {command ? (
        <>
          <div className="mcp-loopback-step">{LOOPBACK_COPY.step1}</div>
          <div className="mcp-loopback-cmd-row">
            <code className="mcp-loopback-cmd">{command}</code>
            <button type="button" className="mcp-tiny-btn" onClick={copy}>{copied ? LOOPBACK_COPY.copied : LOOPBACK_COPY.copy}</button>
          </div>
          <div className="mcp-loopback-step">{LOOPBACK_COPY.step2}</div>
          <div className="mcp-loopback-actions">
            <button type="button" className="mcp-tiny-btn mcp-tiny-btn--primary" onClick={onContinue}>{LOOPBACK_COPY.continue}</button>
            <button type="button" className="mcp-tiny-btn" onClick={onCancel}>{LOOPBACK_COPY.cancel}</button>
          </div>
        </>
      ) : (
        <>
          <div className="mcp-loopback-step mcp-loopback-step--err">{LOOPBACK_COPY.badRelay}</div>
          <div className="mcp-loopback-actions">
            <button type="button" className="mcp-tiny-btn" onClick={onCancel}>{LOOPBACK_COPY.cancel}</button>
          </div>
        </>
      )}
    </div>
  );
}

function ServerCard({
  server,
  driftedTools,
  refreshing,
  deleting,
  confirmingDelete,
  toggling,
  confirmBless,
  serverError,
  serverNote,
  toolDetails,      // [{ name, description, hint, classification, blessed_at }] | null
  onRefresh,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
  onToggleStatus,
  onBlessRequest,
  onBlessConfirm,
  onBlessCancel,
  onConnect,
  onConnectLoopback,
  onDisconnect,
  connecting,
  loopback,
  onLoopbackContinue,
  onLoopbackCancel,
  onBlessAll,
}) {
  const isDisabled = server.status === 'disabled';
  // Residual 23: Refresh is server-refused with 409 oauth_pending on a row that
  // is mid-consent. Don't offer a button whose only outcome is an error.
  const isOAuthPending = server.auth_mode === 'oauth' && server.status !== 'connected';
  const toolEntries = Object.entries(server.tool_pins || {});
  const readCount  = toolEntries.filter(([, p]) => p.classification === 'read').length;
  const gatedCount = toolEntries.filter(([, p]) => p.classification === 'gated').length;
  const hasTools = toolEntries.length > 0;
  // Spec §5: "Bless all read-hinted" — bearer servers only (OAuth is gated by
  // construction), and only tools whose PINNED hint says read-only. The route
  // re-derives this same filter; this is just what the confirm dialog offers.
  const blessable = toolEntries
    .filter(([, p]) => p.classification === 'gated' && p.hints?.readOnlyHint === true)
    .map(([n]) => n);
  const canBlessAll = (server.auth_mode || 'bearer') !== 'oauth' && blessable.length > 0;
  const [toolsOpen, setToolsOpen] = useState(false); // collapsed by default — click header to expand

  return (
    <div className={`mcp-server-card ${isDisabled ? 'mcp-server-card--off' : ''}`}>

      {/* ── Server header row ── */}
      <div className="mcp-server-hd">
        <div
          className={`mcp-server-meta ${hasTools ? 'mcp-server-meta--toggle' : ''}`}
          onClick={hasTools ? () => setToolsOpen((o) => !o) : undefined}
          onKeyDown={hasTools ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setToolsOpen((o) => !o); } } : undefined}
          role={hasTools ? 'button' : undefined}
          tabIndex={hasTools ? 0 : undefined}
          aria-expanded={hasTools ? toolsOpen : undefined}
          title={hasTools ? (toolsOpen ? 'Click to hide tools' : 'Click to show tools') : undefined}
        >
          <div className="mcp-server-name-row">
            {hasTools && (
              <span className={`mcp-tools-caret ${toolsOpen ? 'mcp-tools-caret--open' : ''}`} aria-hidden="true">▸</span>
            )}
            <span className="mcp-server-name">{server.name}</span>
            <StatusBadge status={server.status} />
            {server.hasToken && server.auth_mode !== 'oauth' && (
              <span className="mcp-token-pill" title="Auth token set (write-only — never retrieved)">
                <LockIcon /> token set
              </span>
            )}
            {server.auth_mode === 'oauth' && server.hasOAuthTokens && (
              <span className="mcp-oauth-pill" title="Signed in via OAuth (tokens stored encrypted — never retrieved)">
                <LockIcon /> signed in
              </span>
            )}
          </div>
          <span className="mcp-server-url">{server.url}</span>
          {server.last_error && (
            <span className="mcp-server-err">{lastErrorCopy(server.last_error)}</span>
          )}
          {/* Per-server operation error (refresh / toggle / delete failures) */}
          {serverError && (
            <span className="mcp-server-op-err">{serverError}</span>
          )}
          {/* Per-server operation NOTE — an outcome that is not a failure
              (residual 6: Connect found a session that only needed refreshing). */}
          {serverNote && (
            <span className="mcp-server-op-note">{serverNote}</span>
          )}
          {toolEntries.length > 0 && (
            <span className="mcp-server-summary">
              {readCount > 0 && <span className="mcp-summary-read">{readCount} read</span>}
              {gatedCount > 0 && <span className="mcp-summary-gated">{gatedCount} gated</span>}
            </span>
          )}
        </div>

        <div className="mcp-server-btns">
          {/* Refresh — absent while an OAuth consent is pending (residual 23) */}
          {!isOAuthPending && (
            <button
              className="mcp-icon-btn"
              onClick={() => onRefresh(server.id)}
              disabled={refreshing}
              title="Re-fetch tool list from server"
              aria-label="Refresh tools"
            >
              {refreshing
                ? <span className="mcp-pulse-dot" aria-hidden="true" />
                : <RefreshIcon />}
            </button>
          )}

          {/* Enable / Disable */}
          <button
            className={`mcp-icon-btn ${isDisabled ? 'mcp-icon-btn--enable' : 'mcp-icon-btn--mute'}`}
            onClick={onToggleStatus}
            title={isDisabled ? 'Enable server' : 'Disable server'}
            aria-label={isDisabled ? 'Enable server' : 'Disable server'}
          >
            <PowerIcon />
          </button>

          {server.auth_mode === 'oauth' && !server.hasOAuthTokens && (
            <button
              className="mcp-tiny-btn mcp-tiny-btn--primary"
              onClick={onConnect}
              disabled={connecting}
              title="Sign in to this MCP server"
            >
              {connecting ? '…' : 'Connect'}
            </button>
          )}
          {server.auth_mode === 'oauth' && !server.hasOAuthTokens && !loopback && (
            <button
              className="mcp-tiny-btn"
              onClick={onConnectLoopback}
              disabled={connecting}
              title={LOOPBACK_COPY.title}
            >
              {LOOPBACK_COPY.button}
            </button>
          )}
          {server.auth_mode === 'oauth' && server.hasOAuthTokens && (
            <button
              className="mcp-tiny-btn"
              onClick={onDisconnect}
              disabled={connecting}
              title="Forget the stored OAuth session"
            >
              {connecting ? '…' : 'Disconnect'}
            </button>
          )}

          {/* Delete with inline confirm */}
          {confirmingDelete ? (
            <div className="mcp-inline-confirm">
              <span className="mcp-inline-confirm-txt">Remove?</span>
              <button
                className="mcp-tiny-btn mcp-tiny-btn--danger"
                onClick={onDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? '…' : 'Yes'}
              </button>
              <button className="mcp-tiny-btn" onClick={onDeleteCancel}>No</button>
            </div>
          ) : (
            <button
              className="mcp-icon-btn mcp-icon-btn--danger"
              onClick={onDelete}
              title="Remove server"
              aria-label="Remove server"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── Loopback-assisted connect: the relay command + Continue (never auto-navigates) ── */}
      {loopback && (
        <LoopbackRelayPanel
          loopback={loopback}
          onContinue={onLoopbackContinue}
          onCancel={onLoopbackCancel}
        />
      )}

      {/* ── Tool list (collapsed by default; expands to a scrollable, capped area) ── */}
      {hasTools && toolsOpen ? (
        <div className="mcp-tools">
          <div className="mcp-tools-hd">
            <div className="mcp-tools-hd-labels">
              <span className="mcp-tools-label">Tools ({toolEntries.length})</span>
              <span className="mcp-tools-hint">
                Read = auto-run · Gated = approval required
              </span>
            </div>
            {canBlessAll && (
              <button
                type="button"
                className="mcp-tiny-btn mcp-tiny-btn--primary"
                onClick={() => onBlessAll(server.id, blessable)}
                title="Bless every tool the server marks read-only"
              >
                Bless {blessable.length} read-only {blessable.length === 1 ? 'tool' : 'tools'}
              </button>
            )}
          </div>

          <div className="mcp-tool-rows">
            {toolEntries.map(([toolName, pin]) => {
              const drifted    = driftedTools.includes(toolName);
              const isRead     = pin.classification === 'read';
              const isPaid     = pin.classification === 'paid_read';
              const blessKey   = `${server.id}:${toolName}`;
              const isToggling = !!toggling[blessKey];
              const awaitingBlessConfirm =
                confirmBless?.serverId === server.id &&
                confirmBless?.toolName === toolName;
              // Live tool detail from add/refresh response; fall back to
              // pin-persisted metadata (survives reload) when not yet loaded.
              const live = toolDetails?.find(t => t.name === toolName) || null;
              const pinMeta = pin && pin.description !== undefined
                ? { name: toolName, description: pin.description, hint: pin.hints || {}, classification: pin.classification, blessed_at: pin.blessed_at }
                : null;
              const detail = live || pinMeta;

              return (
                <div
                  key={toolName}
                  className={`mcp-tool-row ${drifted ? 'mcp-tool-row--drifted' : ''}`}
                >
                  <div className="mcp-tool-left">
                    <span className="mcp-tool-name">{toolName}</span>
                    {drifted && (
                      <span className="mcp-drift-pill">
                        <AlertTriangleIcon /> definition changed — re-review
                      </span>
                    )}
                    {isPaid && (
                      <span className="mcp-paid-pill">paid · always approve</span>
                    )}
                    {isRead && pin.blessed_at && (
                      <span className="mcp-blessed-at">
                        blessed {new Date(pin.blessed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  <div className="mcp-tool-right">
                    {awaitingBlessConfirm ? (
                      /* ── Bless confirmation — deliberate, not casual ── */
                      <div className="mcp-bless-confirm">
                        {detail === null ? (
                          /* No tool details loaded yet — block blind blessing */
                          <div className="mcp-bless-no-details">
                            <div className="mcp-bless-no-details-msg">
                              <AlertTriangleIcon />
                              <span>
                                Refresh this server first to review the tool description
                                before blessing it as auto-run.
                              </span>
                            </div>
                            <div className="mcp-bless-confirm-actions">
                              <button className="mcp-tiny-btn" onClick={onBlessCancel}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* Tool details loaded — show description + advisory hint */
                          <>
                            {detail.description && (
                              <div className="mcp-bless-tool-desc">{detail.description}</div>
                            )}
                            {(detail.hint?.readOnlyHint != null ||
                              detail.hint?.destructiveHint != null) && (
                              <div className="mcp-bless-hint">
                                <span className="mcp-bless-hint-label">
                                  server-suggested, unverified —
                                </span>
                                {detail.hint.readOnlyHint != null && (
                                  <span className="mcp-hint-tag">
                                    {detail.hint.readOnlyHint ? 'read-only' : 'may write'}
                                  </span>
                                )}
                                {detail.hint.destructiveHint != null && (
                                  <span className={`mcp-hint-tag ${detail.hint.destructiveHint ? 'mcp-hint-tag--destructive' : ''}`}>
                                    {detail.hint.destructiveHint ? 'destructive' : 'non-destructive'}
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="mcp-bless-confirm-msg">
                              <ShieldIcon />
                              <span>
                                <strong>Auto-run?</strong> This tool will execute
                                without approval in Solomon's agentic loop.
                              </span>
                            </div>
                            <div className="mcp-bless-confirm-actions">
                              <button
                                className="mcp-tiny-btn mcp-tiny-btn--bless"
                                onClick={() => onBlessConfirm(toolName, 'read')}
                                disabled={isToggling}
                                title="Free tool — auto-runs without approval"
                              >
                                <CheckIcon /> Allow free (auto-run)
                              </button>
                              <button
                                className="mcp-tiny-btn mcp-tiny-btn--paid"
                                onClick={() => onBlessConfirm(toolName, 'paid_read')}
                                disabled={isToggling}
                                title="Costs money — always approve each call, never auto-run"
                              >
                                Paid — always approve
                              </button>
                              <button className="mcp-tiny-btn" onClick={onBlessCancel}>
                                Cancel
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        className={`mcp-class-pill ${isRead ? 'mcp-class-pill--read' : isPaid ? 'mcp-class-pill--paid' : 'mcp-class-pill--gated'}`}
                        onClick={() => onBlessRequest(server.id, toolName, pin.classification)}
                        disabled={isToggling}
                        title={
                          isRead
                            ? 'Currently auto-runs — click to revoke (requires approval)'
                            : isPaid
                              ? 'Paid tool — every call is approved individually and may cost money'
                              : 'Currently gated — click to bless (auto-run, no approval)'
                        }
                      >
                        {isToggling ? (
                          <span className="mcp-pulse-dot mcp-pulse-dot--sm" aria-hidden="true" />
                        ) : isRead ? (
                          <><CheckIcon /> Read</>
                        ) : isPaid ? (
                          <><DollarIcon /> Paid</>
                        ) : (
                          <><ShieldIcon /> Gated</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        !hasTools && server.status === 'connected' && (
          <div className="mcp-tools-empty">
            No tools discovered — try refreshing
          </div>
        )
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function McpServersPanel() {
  const { authFetch } = useAuth();

  // Server list
  const [servers,   setServers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [listError, setListError] = useState(null);

  // Add-server form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', token: '', transport: 'http', authHeaderName: '', authRawValue: false });
  const [adding,   setAdding]   = useState(false);
  const [addError, setAddError] = useState(null);

  // Panel-level notice (e.g. "needs sign-in" after register) — rendered above
  // the server list, independent of the add-form's open/closed state, so it
  // survives the form auto-closing on a successful add (Issue 113, Task 10 review).
  const [panelNotice, setPanelNotice] = useState(null);

  // Per-server async state
  const [refreshing,     setRefreshing]     = useState({});  // { [id]: bool }
  const [deleting,       setDeleting]       = useState({});  // { [id]: bool }
  const [confirmDelete,  setConfirmDelete]  = useState(null); // serverId | null
  const [toggling,       setToggling]       = useState({});  // { ['id:tool']: bool }
  const [confirmBless,   setConfirmBless]   = useState(null); // { serverId, toolName } | null
  const [connecting,     setConnecting]     = useState({});  // { [id]: bool }
  const [loopbacks,      setLoopbacks]      = useState({});  // { [id]: { authUrl, relay } } — loopback-assisted connect awaiting Continue

  // Per-server operation errors (refresh / toggle / delete)
  const [serverErrors, setServerErrors] = useState({});   // { [id]: string | null }
  // Successes that are not navigations — kept apart from errors so a good
  // outcome is never rendered in the error colour (residual 6).
  const [serverNotes, setServerNotes] = useState({});     // { [id]: string | null }

  // Live tool details from add/refresh responses. Keyed by serverId.
  // null means "not yet loaded" → bless-blind prevented; [] means "loaded, no tools".
  const [toolDetailsMap, setToolDetailsMap] = useState({}); // { [id]: tool[] | null }

  // Drift (tool definition changed on refresh)
  const [driftMap, setDriftMap] = useState({});  // { [serverId]: [toolName, ...] }

  // ── Load server list ──
  const loadServers = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers`);
      const json = await res.json();
      if (json.success) {
        setServers(json.data || []);
      } else {
        setListError(json.error || 'Could not load MCP servers.');
      }
    } catch {
      setListError('Connection error — could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadServers(); }, [loadServers]);

  // ── Add server ──
  const handleAdd = useCallback(async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    // Beginner-friendly failure message: the #1 cause of a failed connect is a
    // missing/invalid API token, so lead with that instead of a generic "network error".
    const connectHelp = (detail) =>
      `Couldn't connect to that server. The most common reason is a missing or invalid API token — many MCP servers (like Apify) require one. Get a token from the provider's dashboard / settings, paste it in the Auth Token field above, keep Transport on “HTTP”, then try again. If it still fails, double-check the URL.${detail ? ` (Details: ${detail})` : ''}`;
    try {
      const body = {
        name: form.name.trim(),
        url:  form.url.trim(),
        transport: form.transport,
      };
      if (form.token.trim()) body.token = form.token; // token cleared after submit
      if (form.authHeaderName.trim()) {
        body.authHeaderName = form.authHeaderName.trim();
        body.authRawValue = form.authRawValue;
      }
      const res  = await authFetch(`${API_BASE}/api/mcp/servers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.success) {
        // Capture tool details from add response so bless UI is informed immediately
        const serverId = json.data?.server?.id;
        const tools    = json.data?.tools || [];
        if (serverId) {
          setToolDetailsMap(prev => ({ ...prev, [serverId]: tools }));
        }
        if (json.data?.needsAuth) {
          setPanelNotice('Registered. This server needs sign-in — click Connect on its row to finish.');
        }
        setForm({ name: '', url: '', token: '', transport: 'http', authHeaderName: '', authRawValue: false }); // token wiped
        setShowForm(false);
        await loadServers();
      } else {
        setAddError(connectHelp(json.error));
      }
    } catch (err) {
      setAddError(connectHelp(err?.message || 'the server didn’t respond (network error or timeout)'));
    } finally {
      setAdding(false);
    }
  }, [authFetch, form, loadServers]);

  // ── Refresh server tools ──
  const handleRefresh = useCallback(async (serverId) => {
    setRefreshing(prev => ({ ...prev, [serverId]: true }));
    setServerErrors(prev => ({ ...prev, [serverId]: null }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/refresh`, { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        const newDrift = json.data?.drift;
        const newTools = json.data?.tools || [];
        // Unconditionally update drift (clears stale amber when a re-synced server has no drift)
        setDriftMap(prev => ({ ...prev, [serverId]: newDrift || [] }));
        // Store tool details so bless UI can show descriptions going forward
        setToolDetailsMap(prev => ({ ...prev, [serverId]: newTools }));
        await loadServers();
      } else {
        setServerErrors(prev => ({
          ...prev, [serverId]: json.error || 'Could not refresh that server.',
        }));
      }
    } catch {
      setServerErrors(prev => ({
        ...prev, [serverId]: 'Connection error — could not reach the server.',
      }));
    } finally {
      setRefreshing(prev => ({ ...prev, [serverId]: false }));
    }
  }, [authFetch, loadServers]);

  // ── OAuth connect / disconnect (Issue 113) ──
  const handleConnect = useCallback(async (serverId) => {
    setConnecting(prev => ({ ...prev, [serverId]: true }));
    setServerErrors(prev => ({ ...prev, [serverId]: null }));
    setServerNotes(prev => ({ ...prev, [serverId]: null }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/oauth/start`, { method: 'POST' });
      const json = await res.json();
      if (json.success && json.data?.authUrl) {
        window.location.href = json.data.authUrl; // forward-only: we leave, the callback brings us back
        return;
      }
      // Residual 6: the stored session only needed refreshing — a success with
      // nowhere to send the browser, so it must not render as a failed start.
      if (json.success && json.data?.code === 'already_connected') {
        setServerNotes(prev => ({ ...prev, [serverId]: lastErrorCopy('already_connected') }));
        await loadServers();
        return;
      }
      setServerErrors(prev => ({ ...prev, [serverId]: lastErrorCopy(json.error) || 'Could not start sign-in.' }));
    } catch {
      setServerErrors(prev => ({ ...prev, [serverId]: 'Connection error — could not start sign-in.' }));
    } finally {
      setConnecting(prev => ({ ...prev, [serverId]: false }));
    }
  }, [authFetch, loadServers]);

  // Loopback-assisted connect: same start route with { loopback: true }; the
  // response carries the relay contract and we STOP — no navigation until the
  // operator has the relay running and presses Continue.
  const handleConnectLoopback = useCallback(async (serverId) => {
    setConnecting(prev => ({ ...prev, [serverId]: true }));
    setServerErrors(prev => ({ ...prev, [serverId]: null }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/oauth/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loopback: true }),
      });
      const json = await res.json();
      if (json.success && json.data?.authUrl && json.data?.relay) {
        setLoopbacks(prev => ({ ...prev, [serverId]: { authUrl: json.data.authUrl, relay: json.data.relay } }));
        return;
      }
      setServerErrors(prev => ({ ...prev, [serverId]: lastErrorCopy(json.error) || 'Could not start sign-in.' }));
    } catch {
      setServerErrors(prev => ({ ...prev, [serverId]: 'Connection error — could not start sign-in.' }));
    } finally {
      setConnecting(prev => ({ ...prev, [serverId]: false }));
    }
  }, [authFetch]);

  const handleLoopbackContinue = useCallback((serverId) => {
    const pending = loopbacks[serverId];
    if (!pending?.authUrl) return;
    window.location.href = pending.authUrl; // forward-only: the relay + callback bring us back
  }, [loopbacks]);

  const handleLoopbackCancel = useCallback((serverId) => {
    setLoopbacks(prev => { const next = { ...prev }; delete next[serverId]; return next; });
  }, []);

  const handleDisconnect = useCallback(async (serverId) => {
    setConnecting(prev => ({ ...prev, [serverId]: true }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/oauth/disconnect`, { method: 'POST' });
      const json = await res.json();
      if (!json.success) setServerErrors(prev => ({ ...prev, [serverId]: json.error || 'Could not disconnect.' }));
      await loadServers();
    } catch {
      setServerErrors(prev => ({ ...prev, [serverId]: 'Connection error — could not disconnect.' }));
    } finally {
      setConnecting(prev => ({ ...prev, [serverId]: false }));
    }
  }, [authFetch, loadServers]);

  // ── Toggle enable / disable ──
  const handleToggleStatus = useCallback(async (server) => {
    const newStatus = server.status === 'disabled' ? 'connected' : 'disabled';
    setServerErrors(prev => ({ ...prev, [server.id]: null }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${server.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (json.success) {
        await loadServers();
      } else {
        setServerErrors(prev => ({
          ...prev, [server.id]: json.error || 'Could not change server status.',
        }));
      }
    } catch {
      setServerErrors(prev => ({
        ...prev, [server.id]: 'Connection error — could not reach the server.',
      }));
    }
  }, [authFetch, loadServers]);

  // ── Bless/revoke a tool ──
  // applyToolChange is declared BEFORE handleBlessRequest so it can be in the dep array.
  const applyToolChange = useCallback(async (serverId, toolName, classification) => {
    const key = `${serverId}:${toolName}`;
    setToggling(prev => ({ ...prev, [key]: true }));
    setConfirmBless(null);
    setServerErrors(prev => ({ ...prev, [serverId]: null }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/tools`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toolName, classification }),
      });
      const json = await res.json();
      if (json.success) {
        // If re-blessed after drift, clear the drift indicator for this tool
        if (classification === 'read') {
          setDriftMap(prev => {
            const tools = (prev[serverId] || []).filter(t => t !== toolName);
            return tools.length
              ? { ...prev, [serverId]: tools }
              : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== serverId));
          });
        }
        await loadServers();
      } else {
        setServerErrors(prev => ({
          ...prev, [serverId]: lastErrorCopy(json.error) || 'Could not update tool classification.',
        }));
      }
    } catch {
      setServerErrors(prev => ({
        ...prev, [serverId]: 'Connection error — could not reach the server.',
      }));
    } finally {
      setToggling(prev => ({ ...prev, [key]: false }));
    }
  }, [authFetch, loadServers]);

  const handleBlessRequest = useCallback((serverId, toolName, currentClass) => {
    if (currentClass === 'gated') {
      // Blessing to read — requires deliberate confirmation (security gate)
      setConfirmBless({ serverId, toolName });
    } else {
      // Revoking to gated — safer direction, no confirmation needed
      applyToolChange(serverId, toolName, 'gated');
    }
  }, [applyToolChange]);

  // ── Bless all read-hinted tools (bearer servers only, spec §5) ──
  const handleBlessAll = useCallback(async (serverId, names) => {
    if (!window.confirm(`Bless these tools to auto-run without approval?\n\n${names.join('\n')}`)) return;
    setServerErrors(prev => ({ ...prev, [serverId]: null }));
    try {
      const res = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}/tools`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toolNames: names, classification: 'read' }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.success) {
        await loadServers();
      } else {
        setServerErrors(prev => ({ ...prev, [serverId]: lastErrorCopy(json.error) || 'Could not bless those tools.' }));
      }
    } catch {
      setServerErrors(prev => ({ ...prev, [serverId]: 'Connection error — could not reach the server.' }));
    }
  }, [authFetch, loadServers]);

  // ── Delete server ──
  const handleDelete = useCallback(async (serverId) => {
    setDeleting(prev => ({ ...prev, [serverId]: true }));
    setServerErrors(prev => ({ ...prev, [serverId]: null }));
    try {
      const res  = await authFetch(`${API_BASE}/api/mcp/servers/${serverId}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        setConfirmDelete(null);
        setDriftMap(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== serverId)));
        setToolDetailsMap(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== serverId)));
        await loadServers();
      } else {
        setServerErrors(prev => ({
          ...prev, [serverId]: json.error || 'Could not delete that server.',
        }));
      }
    } catch {
      setServerErrors(prev => ({
        ...prev, [serverId]: 'Connection error — could not reach the server.',
      }));
    } finally {
      setDeleting(prev => ({ ...prev, [serverId]: false }));
    }
  }, [authFetch, loadServers]);

  // ── Chat-scoped grants ("Allow for this chat") ──
  const [grants, setGrants] = useState([]);
  const loadGrants = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/mcp/grants`);
      if (!res.ok) return;
      const { data } = await res.json();
      setGrants(Array.isArray(data) ? data : []);
    } catch { /* fail-soft: the list simply doesn't render */ }
  }, [authFetch]);
  useEffect(() => { loadGrants(); }, [loadGrants]);

  const revokeGrant = useCallback(async (grantId) => {
    const res = await authFetch(`${API_BASE}/api/mcp/grants/${grantId}`, { method: 'DELETE' });
    if (res.ok) setGrants((prev) => prev.filter((g) => g.id !== grantId));
  }, [authFetch]);

  // ── Render ──
  return (
    <div className="mcp-panel">

      {/* Section intro */}
      <div className="mcp-panel-intro">
        <p className="mcp-panel-desc">
          Connect remote tool servers via the Model Context Protocol.
          <br />
          <span className="mcp-panel-desc-key read-key">Read</span> tools auto-run in Solomon's agentic loop.{' '}
          <span className="mcp-panel-desc-key gated-key">Gated</span> tools require your approval.
          Blessing a tool as Read is a security decision — do it deliberately.
        </p>
        <button
          className={`mcp-add-trigger ${showForm ? 'mcp-add-trigger--active' : ''}`}
          onClick={() => { setShowForm(v => !v); setAddError(null); }}
        >
          {showForm ? '✕  Cancel' : '+ Add Server'}
        </button>
      </div>

      {/* Add-server form */}
      {showForm && (
        <form className="mcp-add-form" onSubmit={handleAdd} noValidate>

          <div className="mcp-field">
            <label className="mcp-field-label" htmlFor="mcp-name">Name</label>
            <input
              id="mcp-name"
              className="mcp-input"
              type="text"
              placeholder="e.g. GitHub Actions"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
              disabled={adding}
              autoComplete="off"
            />
          </div>

          <div className="mcp-field">
            <label className="mcp-field-label" htmlFor="mcp-url">
              URL <span className="mcp-field-sub">(HTTPS required)</span>
            </label>
            <input
              id="mcp-url"
              className="mcp-input"
              type="url"
              placeholder="https://mcp.example.com/mcp"
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              required
              disabled={adding}
              autoComplete="off"
            />
          </div>

          <div className="mcp-field">
            <label className="mcp-field-label" htmlFor="mcp-token">
              Auth Token <span className="mcp-field-sub">(optional · write-only, never retrieved)</span>
            </label>
            <div className="mcp-token-wrapper">
              <LockIcon />
              <input
                id="mcp-token"
                className="mcp-input mcp-input--token"
                type="password"
                placeholder="Bearer token or API key"
                value={form.token}
                onChange={e => setForm(f => ({ ...f, token: e.target.value }))}
                autoComplete="new-password"
                disabled={adding}
              />
            </div>
            <p className="mcp-field-sub mcp-field-help">
              Many servers need an API token (e.g. Apify, GitHub) — get one from the provider's dashboard / settings and paste it here. Leave blank only for public servers that need no auth.
            </p>
          </div>

          <div className="mcp-field">
            <label className="mcp-field-label" htmlFor="mcp-transport">Transport</label>
            <select
              id="mcp-transport"
              className="mcp-input mcp-select"
              value={form.transport}
              onChange={e => setForm(f => ({ ...f, transport: e.target.value }))}
              disabled={adding}
            >
              <option value="http">HTTP — Streamable HTTP (recommended)</option>
              <option value="sse">SSE — Server-Sent Events (legacy)</option>
            </select>
          </div>

          <div className="mcp-field">
            <label className="mcp-field-label" htmlFor="mcp-auth-header">Auth header (advanced)</label>
            <input
              id="mcp-auth-header"
              className="mcp-input"
              type="text"
              placeholder="Authorization (default) — or e.g. X-API-Key"
              value={form.authHeaderName}
              onChange={e => setForm(f => ({ ...f, authHeaderName: e.target.value }))}
              disabled={adding}
            />
            <label className="mcp-field-sub mcp-field-help">
              <input
                type="checkbox"
                checked={form.authRawValue}
                onChange={e => setForm(f => ({ ...f, authRawValue: e.target.checked }))}
                disabled={adding}
              />{' '}
              Send token as raw header value (no “Bearer ” prefix)
            </label>
          </div>

          {addError && (
            <div className="mcp-error-msg" role="alert">{addError}</div>
          )}

          <button className="mcp-btn-primary" type="submit" disabled={adding}>
            {adding ? (
              <span className="mcp-connecting">
                <span className="mcp-breathe-ring" aria-hidden="true" />
                Connecting…
              </span>
            ) : 'Connect Server'}
          </button>
        </form>
      )}

      {/* Panel-level notice (e.g. "needs sign-in" after register) — lives above
          the server list so it survives the add-form closing. */}
      {panelNotice && (
        <div className="mcp-panel-notice" role="status">
          <span>{panelNotice}</span>
          <button
            type="button"
            className="mcp-panel-notice-dismiss"
            onClick={() => setPanelNotice(null)}
            aria-label="Dismiss notice"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Server list */}
      {loading ? (
        <div className="mcp-loading" aria-live="polite">
          <span className="mcp-breathe-ring" aria-hidden="true" />
          <span>Loading servers…</span>
        </div>
      ) : listError ? (
        <div className="mcp-error-msg" role="alert">{listError}</div>
      ) : servers.length === 0 ? (
        <div className="mcp-empty">
          No MCP servers connected yet.{' '}
          {!showForm && (
            <button className="mcp-empty-link" onClick={() => setShowForm(true)}>
              Add one above.
            </button>
          )}
        </div>
      ) : (
        <div className="mcp-server-list">
          {servers.map(server => (
            <ServerCard
              key={server.id}
              server={server}
              driftedTools={driftMap[server.id] || []}
              refreshing={!!refreshing[server.id]}
              deleting={!!deleting[server.id]}
              confirmingDelete={confirmDelete === server.id}
              toggling={toggling}
              confirmBless={confirmBless}
              serverError={serverErrors[server.id] || null}
              serverNote={serverNotes[server.id] || null}
              toolDetails={toolDetailsMap[server.id] ?? null}
              onRefresh={handleRefresh}
              onDelete={() => setConfirmDelete(server.id)}
              onDeleteConfirm={() => handleDelete(server.id)}
              onDeleteCancel={() => setConfirmDelete(null)}
              onToggleStatus={() => handleToggleStatus(server)}
              onBlessRequest={handleBlessRequest}
              onBlessConfirm={(toolName, classification = 'read') => applyToolChange(server.id, toolName, classification)}
              onBlessCancel={() => setConfirmBless(null)}
              onConnect={() => handleConnect(server.id)}
              onConnectLoopback={() => handleConnectLoopback(server.id)}
              onDisconnect={() => handleDisconnect(server.id)}
              connecting={!!connecting[server.id]}
              loopback={loopbacks[server.id] || null}
              onLoopbackContinue={() => handleLoopbackContinue(server.id)}
              onLoopbackCancel={() => handleLoopbackCancel(server.id)}
              onBlessAll={handleBlessAll}
            />
          ))}
        </div>
      )}

      {/* Chat-scoped grants ("Allow for this chat") — spec §4.3 */}
      <section className="mcp-grants">
        <h4 className="mcp-grants-title">Allowed for a chat</h4>
        {grants.length === 0 ? (
          <p className="sol-empty">No chat-scoped grants.</p>
        ) : (
          <ul className="mcp-grants-list">
            {grants.map((g) => (
              <li key={g.id} className="mcp-grants-row">
                <span className="mcp-grants-tool">{g.server_name} / {g.tool_name}</span>
                <button className="mcp-tiny-btn mcp-tiny-btn--danger" onClick={() => revokeGrant(g.id)}>Revoke</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
